// src/creator/creatorTripsData.js
// Creator Trips demo data + the origin cities every trip page (creator and curated) plans from.
//
// RECONSTRUCTED FILE: the original was lost when src/ was replaced. Same exports and data shape
// as before (CREATORS, getCreator, getCreatorTrips, getTrip, ORIGIN_CITIES); the demo creator and
// trips below are placeholders. If you recover your original from Trash / Time Machine / editor
// history, prefer it, but keep the leave_after fields and the ORIGIN_CITIES list in sync.
//
// Trip template shape (read by creatorTripEngine.buildInstance and CreatorTripPage):
//   slug, creator, title, subtitle, cover, reel_url, reel_views, vibe[], max_group, creator_markup_pp,
//   duration_days, season? { from: "MM-DD", to: "MM-DD", note },
//   entry { city, name, lat, lng, start_by "HH:MM" }, exit { city, name, lat, lng, day_offset, leave_after "HH:MM" },
//   days[{ day, title, blocks[] }]
// Blocks:
//   STAY { city, nights, preferred, tier_max_pn, fallback_pn, lock }
//   MOVE { mode, from, to, label, depart, duration, price_pp, overnight?, lock }
//   STOP / EXPERIENCE { name, time, note, lock }    TIP { text, reel_ts? }
// lock: LOCKED (creator pick, never changes) | PREFERRED | FLEXIBLE

// Cities a follower can start from. Keep names stable: curated trips match their default_origin against these.
export const ORIGIN_CITIES = [
    { name: "Bengaluru", lat: 12.9716, lng: 77.5946 },
    { name: "Mumbai", lat: 19.076, lng: 72.8777 },
    { name: "Delhi", lat: 28.6139, lng: 77.209 },
    { name: "Hyderabad", lat: 17.385, lng: 78.4867 },
    { name: "Chennai", lat: 13.0827, lng: 80.2707 },
    { name: "Kolkata", lat: 22.5726, lng: 88.3639 },
    { name: "Pune", lat: 18.5204, lng: 73.8567 },
    { name: "Ahmedabad", lat: 23.0225, lng: 72.5714 },
    { name: "Kochi", lat: 9.9312, lng: 76.2673 },
    { name: "Jaipur", lat: 26.9124, lng: 75.7873 },
    { name: "Chandigarh", lat: 30.7333, lng: 76.7794 },
    { name: "Lucknow", lat: 26.8467, lng: 80.9462 },
    { name: "Indore", lat: 22.7196, lng: 75.8577 },
    { name: "Mysuru", lat: 12.2958, lng: 76.6394 },
];

// Soft gradient covers as inline SVG, so the demo needs no image hosting. Swap for real reel thumbnails.
const cover = (a, b) => `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="1200" height="600"><defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${a}"/><stop offset="1" stop-color="${b}"/></linearGradient></defs><rect width="1200" height="600" fill="url(#g)"/></svg>`,
)}`;

export const CREATORS = {
    wanderwithria: {
        handle: "wanderwithria",
        name: "Ria (demo creator)",
        avatar_initials: "R",
        followers: "214K",
        platform: "Instagram",
        bio: "Slow coastal trips, good food, no 5 am alarms. Every trip here is one I've done myself, bookable from your city.",
        trips_booked: 1240,
        rating: 4.8,
    },
};

const TRIPS = [
    {
        slug: "gokarna-beach-hop",
        creator: "wanderwithria",
        title: "Gokarna Beach Hop",
        subtitle: "3 days · Om, Kudle and Paradise beaches",
        cover: cover("#0E7490", "#F59E0B"),
        reel_url: "https://www.instagram.com/",
        reel_views: "1.2M",
        vibe: ["Beaches", "Cafes", "Easy treks"],
        max_group: 6,
        creator_markup_pp: 1500,
        duration_days: 3,
        season: { from: "10-01", to: "05-15", note: "Monsoon (Jun to Sep) closes most beach shacks and the trek between beaches." },
        entry: { city: "Gokarna", name: "Gokarna town", lat: 14.5479, lng: 74.3188, start_by: "11:00" },
        exit: { city: "Gokarna", name: "Gokarna town", lat: 14.5479, lng: 74.3188, day_offset: 2, leave_after: "14:00" },
        days: [
            {
                day: 1, title: "Kudle beach sunset",
                blocks: [
                    { type: "STAY", city: "Gokarna", nights: 2, preferred: "Zostel Gokarna", tier_max_pn: 4500, fallback_pn: 3500, lock: "PREFERRED" },
                    { type: "STOP", name: "Mahabaleshwar Temple", time: "12:30", note: "Dress modestly, shoes off at the gate", lock: "FLEXIBLE" },
                    { type: "STOP", name: "Kudle beach sunset", time: "17:30", note: "Grab a seat at the cliff cafes", lock: "LOCKED" },
                    { type: "TIP", text: "Carry cash. Many shacks on the beaches don't take cards.", reel_ts: "0:42" },
                ],
            },
            {
                day: 2, title: "Beach trek day",
                blocks: [
                    { type: "EXPERIENCE", name: "Om to Paradise beach trek", time: "07:30", note: "About 6 km, boat back to Om", lock: "LOCKED" },
                    { type: "STOP", name: "Half Moon beach lunch", time: "12:30", lock: "FLEXIBLE" },
                    { type: "TIP", text: "Start early. The rocks between Half Moon and Paradise get very hot by noon.", reel_ts: "1:15" },
                ],
            },
            {
                day: 3, title: "Slow morning, head home",
                blocks: [
                    { type: "STOP", name: "Breakfast at Namaste Cafe, Om beach", time: "09:00", lock: "FLEXIBLE" },
                ],
            },
        ],
    },
    {
        slug: "pondicherry-weekend",
        creator: "wanderwithria",
        title: "Pondicherry Weekend",
        subtitle: "2 days · White Town, Auroville, Serenity beach",
        cover: cover("#B45309", "#0369A1"),
        reel_url: "https://www.instagram.com/",
        reel_views: "860K",
        vibe: ["Heritage", "Cafes", "Cycling"],
        max_group: 6,
        creator_markup_pp: 1000,
        duration_days: 2,
        entry: { city: "Puducherry", name: "Puducherry", lat: 11.9416, lng: 79.8083, start_by: "10:00" },
        exit: { city: "Puducherry", name: "Puducherry", lat: 11.9416, lng: 79.8083, day_offset: 1, leave_after: "15:00" },
        days: [
            {
                day: 1, title: "White Town on a cycle",
                blocks: [
                    { type: "STAY", city: "Puducherry", nights: 1, preferred: "Le Dupleix", tier_max_pn: 9000, fallback_pn: 6500, lock: "PREFERRED" },
                    { type: "EXPERIENCE", name: "White Town heritage cycle tour", time: "16:00", note: "Rent a cycle on Mission Street", lock: "LOCKED" },
                    { type: "STOP", name: "Promenade beach at night", time: "20:00", lock: "FLEXIBLE" },
                ],
            },
            {
                day: 2, title: "Auroville and the beach",
                blocks: [
                    { type: "STOP", name: "Matrimandir viewing point, Auroville", time: "09:00", note: "Inner chamber needs a prior booking", lock: "PREFERRED" },
                    { type: "STOP", name: "Serenity beach", time: "12:00", lock: "FLEXIBLE" },
                    { type: "TIP", text: "Most cafes in White Town close between 3 and 6 pm.", reel_ts: "0:58" },
                ],
            },
        ],
    },
];

export const getCreator = (handle) => CREATORS[handle] || null;
export const getCreatorTrips = (handle) => TRIPS.filter((t) => t.creator === handle);
export const getTrip = (slug) => TRIPS.find((t) => t.slug === slug) || null;
